import type { MockProfile } from '@/data/mockProfiles';
import type { VipProfile } from '@/modules/vip/types';
import type { TvCreator, TvSeries } from '@/modules/tv/types';

/**
 * Unified feed item — the .app feed mixes .app profiles, .vip cross-promo,
 * .tv series, and .tv creator cards. Each variant routes to its own
 * surface and carries a small surface badge.
 */
export type FeedItem =
  | { kind: 'app_profile'; id: string; profile: MockProfile }
  | { kind: 'vip_profile'; id: string; profile: VipProfile }
  | { kind: 'tv_series'; id: string; series: TvSeries }
  | { kind: 'tv_creator'; id: string; creator: TvCreator };

interface ComposeArgs {
  profiles: MockProfile[];
  vipProfiles: VipProfile[];
  tvSeries: TvSeries[];
  tvCreators: TvCreator[];
  /** How many .app profiles between cross-promo items (default 4). */
  cadence?: number;
  /** Maximum cross-promo items to include in total (default 8). */
  maxPromos?: number;
}

/**
 * Interleave cross-promotional items into the .app feed at a fixed cadence.
 *
 * The composer is deterministic given the same inputs — no random shuffle
 * inside. Caller can decide whether to memoize (FeedPage does, keyed on
 * the input lengths).
 */
export const composeFeed = ({
  profiles,
  vipProfiles,
  tvSeries,
  tvCreators,
  cadence = 4,
  maxPromos = 8,
}: ComposeArgs): FeedItem[] => {
  // Build cross-promo queue: interleave by type so we don't drop 3 .vip
  // cards in a row.
  const promoQueue: FeedItem[] = [];
  const vipQueue: FeedItem[] = vipProfiles.map((p) => ({
    kind: 'vip_profile',
    id: `vip-${p.id}`,
    profile: p,
  }));
  const tvSeriesQueue: FeedItem[] = tvSeries.map((s) => ({
    kind: 'tv_series',
    id: `tv-series-${s.id}`,
    series: s,
  }));
  const tvCreatorsQueue: FeedItem[] = tvCreators.map((c) => ({
    kind: 'tv_creator',
    id: `tv-creator-${c.id}`,
    creator: c,
  }));
  // Round-robin interleave the three sources
  while (
    promoQueue.length < maxPromos &&
    (vipQueue.length > 0 || tvSeriesQueue.length > 0 || tvCreatorsQueue.length > 0)
  ) {
    if (vipQueue.length > 0) promoQueue.push(vipQueue.shift()!);
    if (promoQueue.length >= maxPromos) break;
    if (tvSeriesQueue.length > 0) promoQueue.push(tvSeriesQueue.shift()!);
    if (promoQueue.length >= maxPromos) break;
    if (tvCreatorsQueue.length > 0) promoQueue.push(tvCreatorsQueue.shift()!);
  }

  const out: FeedItem[] = [];
  profiles.forEach((p, i) => {
    out.push({ kind: 'app_profile', id: p.id, profile: p });
    if ((i + 1) % cadence === 0 && promoQueue.length > 0) {
      out.push(promoQueue.shift()!);
    }
  });
  // Append any remaining promos so the cross-promo set always shows
  out.push(...promoQueue);
  return out;
};
